/**
 * engine-config.ts — silnik formatek: kontrakt konfiguracji per kartoteka
 * (keystone unifikacja-formatek, Krok 1 — uogólnienie silnika pilota).
 *
 * Silnik renderowania pól (descent `RecursiveFormRenderer` + zapis
 * `saveAggregate`) pozostaje WSPÓLNY. Każda kartoteka dostarcza tylko
 * `KartotekaEngineConfig<TForm>` — mapę pól core, nadpisań (custom widgety),
 * nazwanych paneli (quirki poziomu zakładki) i wskazówek prezentacji.
 *
 * Ten plik zawiera WYŁĄCZNIE typy (zero JSX) — konsumuje je generyczny hook
 * `useKartotekaRenderField` (silnik) oraz per-kartotekowe pliki configu
 * (`documentEngineConfig.tsx` = pilot commercial/internal; faktury w Kroku 2).
 *
 * Uogólnienie jest ADDYTYWNE (decyzja Z2): pilot da się wyrazić jako jedna
 * instancja tego configu odtwarzająca dziś-stan bajt-w-bajt (etykiety i18n,
 * `testIdPrefix`, koercja pustych, fallback 403) — patrz `documentEngineConfig.tsx`.
 */

import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';

import type { LayoutElement } from '@/components/form/RecursiveFormRenderer';
import { collectFieldFormatErrors, type FieldFormatCheck } from '@/lib/field-format-validation';
import {
  collectFieldRuleErrors,
  type FieldRuleCheck,
  resolveRuleErrorMessage,
} from '@/lib/field-rule-validation';
import { slugToCamel } from '@/lib/slug-to-camel';

import type { EngineFormField } from './engine-types';

/**
 * Kontekst przekazywany do renderera pola core z nadpisaniem (custom widget).
 * Nadzbiór potrzeb pilota (form/onChange/editable/isCreate) i faktur
 * (companyId dla pickera kontrahenta, hasError/clearError dla podświetlenia
 * pól wymaganych). `TForm` = kształt stanu nagłówka danej kartoteki.
 */
export interface FieldRenderContext<TForm, TExtra = undefined> {
  /** Definicja pola z GET /system-modules/:slug/fields (etykieta = field.name gdy labelSource='definition'). */
  field: EngineFormField;
  /** Wartość pola w stanie (`form[stateKey]`) — wygoda; renderery mogą czytać `form` wprost. */
  value: unknown;
  /** Pełny stan nagłówka (pickery łańcuchowe, warunki disable). */
  form: TForm;
  /** Atomowy zapis N pól jednym patchem — subsumuje `set`/`setMany` faktur. */
  setField: (patch: Partial<TForm>) => void;
  editable: boolean;
  isCreate: boolean;
  /** Prefiks testid kartoteki (pilot: `${docClass}-doc-field`). */
  testIdPrefix: string;
  /**
   * Firma dla pickerów zależnych od firmy (vendor/owner) — T3 company scoping,
   * CLAUDE.md §4.8. Create: firma wybrana w kreatorze typu dokumentu (z URL);
   * edit: firma zapisana na rekordzie (`detailQ.data.companyId`) — NIE tylko
   * create, jak wcześniej (bug zamknięty w code review T3, [E]: faktury nie
   * przekazywały companyId w edit). `undefined`/`null` = brak kontekstu firmy
   * (picker nieograniczony, nigdy blokowany).
   */
  companyId?: string | null;
  /** Podświetlenie pola wymaganego (różnica per-config faktur; pilot nie używa). */
  hasError?: (key: keyof TForm) => boolean;
  /**
   * Komunikat błędu walidacji pod polem (czerwony tekst — konkretny powód po
   * polsku). Analogiczny do `hasError` (ramka), ale zwraca tekst; renderer
   * przekazuje wynik do `<Field error={...}>`. `useFieldErrors` po stronie strony.
   */
  getError?: (key: keyof TForm) => string | undefined;
  /** Czyszczenie błędu przy edycji (jw.). */
  clearError?: (key: keyof TForm) => void;
  /**
   * Predykat „czy to pole jest dodatkowo wymagane" — neutralny z perspektywy
   * silnika (nie wie DLACZEGO). Każda kartoteka wstrzykuje własną semantykę:
   * pilot/listy/pismo — pole daty numeracji; faktury — j.w. Zawsze obecny na
   * ctx (silnik podstawia `() => false`, gdy runtime go nie poda).
   */
  isFieldExtraRequired: (field: EngineFormField) => boolean;
  /** Nieprzezroczysty stan runtime specyficzny dla kartoteki (faktury: `{ hasItems }`). Silnik go nie interpretuje. */
  extra?: TExtra;
  t: TFunction;
}

/** Renderer custom widgetu pola core (status enum, DictionarySelect, async NIP, PESEL-za-uprawnieniem, checkbox-clear, disable warunkowy). */
export type FieldRenderer<TForm, TExtra = undefined> = (
  ctx: FieldRenderContext<TForm, TExtra>,
) => ReactNode;

/** Predykat „pole ma dany slug" — wspólna implementacja `isFieldExtraRequired` dla kartotek numerujących po jednym polu daty (pilot/listy/pismo/faktury). */
export const isSlugMatch =
  (slug?: string) =>
  (f: EngineFormField): boolean =>
    f.slug === slug;

/**
 * Konfiguracja pojedynczego pola core kartoteki (slug → config). Struktura
 * scala mapowanie stanu (stateKey), wskazówki prezentacji (colSpan/maxLength/
 * rows), ground-truth fieldType (fallback 403) oraz — dla quirków — renderer
 * lub flagę „strukturalne" (renderField zwraca null).
 */
export interface CoreFieldConfig<TForm, TExtra = undefined> {
  /** Klucz w stanie nagłówka. Pominięty tylko dla pól strukturalnych (structuralNull). */
  stateKey?: keyof TForm;
  /** Ground-truth `fieldType` z seeda — używany przez fallback statyczny (403 na metadanych modułu). */
  fieldType: string;
  /** Placement — trafia na <Field colSpan>. Domyślnie 1. */
  colSpan?: 1 | 2;
  /** Limit dla varchar/text (przekazany do ExtFieldValueInput). */
  maxLength?: number;
  /** rows dla widgetu 'text' (Textarea). */
  rows?: number;
  /** Jeśli obecny → renderuj custom widget zamiast delegacji do ExtFieldValueInput. */
  renderer?: FieldRenderer<TForm, TExtra>;
  /** Strukturalne: renderField zwraca null (np. documentTypeId — badge w nagłówku, poza drzewem layoutu). */
  structuralNull?: boolean;
}

/**
 * Kontekst nazwanego panelu (quirk poziomu zakładki/panelu — KSeF/Symfonia/CRU).
 * Layout mówi GDZIE (element `panel_view` z `settings.panelKey`), config mówi CO.
 * Renderer czyta `element.settings` per instancja.
 */
export interface NamedPanelContext {
  element: LayoutElement;
  recordId: string | null;
  editable: boolean;
  companyId?: string | null;
}

export type NamedPanelRenderer = (ctx: NamedPanelContext) => ReactNode;

/** Runtime silnika — stan i callbacki strony (odseparowane od statycznego configu). */
export interface KartotekaRenderRuntime<TForm, TExtra = undefined> {
  form: TForm;
  onChange: (next: TForm) => void;
  /** Stan wartości pól dodanych (slug → wartość), kontrolowany przez stronę. */
  extValues: Record<string, unknown>;
  onExtChange: (slug: string, value: unknown) => void;
  editable: boolean;
  isCreate: boolean;
  companyId?: string | null;
  hasError?: (key: keyof TForm) => boolean;
  /** Komunikat błędu walidacji pod polem (analogiczny do `hasError`, zwraca tekst). `useFieldErrors`. */
  getError?: (key: keyof TForm) => string | undefined;
  clearError?: (key: keyof TForm) => void;
  /** Nieprzezroczysty stan runtime specyficzny dla kartoteki (faktury: `{ hasItems }`). Silnik go nie interpretuje. */
  extra?: TExtra;
  /**
   * Predykat „czy to pole jest dodatkowo wymagane" (gwiazdka), wstrzykiwany przez
   * kartotekę. `undefined` → silnik podstawia `() => false` (brak gwiazdek
   * dodatkowych, np. Produkty).
   */
  isFieldExtraRequired?: (field: EngineFormField) => boolean;
}

export interface KartotekaRenderResult {
  /**
   * Renderuje pole po id. Opcjonalny `settings` (z `el.settings` układu kreatora)
   * niesie nadpisania etykiety/wymagalności dla pól dodatkowych — patrz
   * `RecursiveFormRenderer`, który je przekazuje.
   */
  renderField: (fieldId: string, settings?: Record<string, unknown>) => ReactNode;
  fields: EngineFormField[];
  isLoading: boolean;
}

/**
 * Argumenty resolvera etykiety pola natywnego (delegowanego do
 * ExtFieldValueInput). `labelSource: 'definition'` (domyślne, Z3) → field.name;
 * pilot podaje FUNKCJĘ zwracającą etykietę z i18n (zachowanie bez zmian).
 */
export interface LabelSourceArgs<TForm, TExtra = undefined> {
  field: EngineFormField;
  cfg: CoreFieldConfig<TForm, TExtra>;
  t: TFunction;
}

/** Statyczna konfiguracja kartoteki podawana silnikowi. */
export interface KartotekaEngineConfig<TForm, TExtra = undefined> {
  /** Slug modułu systemowego — GET /system-modules/:slug/fields + klucz cache. */
  moduleSlug: string;
  /** Prefiks testid (pilot: `${docClass}-doc-field`). */
  testIdPrefix: string;
  /** Slugi core w kolejności seeda — synteza zakładki 'data' przez top-up (konsumuje strona). */
  coreFieldSlugs: string[];
  /** Mapa slug → config pola core (OTWARTA — nowy slug wchodzi bez zmiany silnika). */
  fields: Record<string, CoreFieldConfig<TForm, TExtra>>;
  /**
   * Pola WIRTUALNE (wyliczane, tylko-do-odczytu) NIE istniejące w katalogu
   * `system_module_fields` — silnik dokłada je do `effectiveFields` ZAWSZE (niezależnie
   * od odpowiedzi `/fields`), żeby `renderField(slug)` je odnalazł. Renderer takiego pola
   * musi być w `fields[slug]`. Dziś: kolumny procesu faktur (aktywny proces / bieżący
   * etap) — patrz `lib/processes/computed-fields.ts` + ADR-0093. Domyślnie brak — inne
   * kartoteki działają bez zmian.
   */
  virtualFields?: EngineFormField[];
  /**
   * Źródło etykiet pól NATYWNYCH (delegowanych do ExtFieldValueInput).
   * 'definition' (domyślne) = field.name; funkcja = etykieta własna (pilot: i18n).
   * Nadpisania (renderer) i tak niosą etykietę we własnym JSX.
   */
  labelSource?: 'definition' | ((args: LabelSourceArgs<TForm, TExtra>) => string);
  /** Nazwane panele (quirki): panelKey → renderer. Layout mówi GDZIE, config CO. */
  panels?: Record<string, NamedPanelRenderer>;
  /** Semantyka `'' → null` przy zapisie pól natywnych (pilot: true). */
  coerceEmptyToNull?: boolean;
}

/**
 * F22 (widoki dodatkowe, PHASE_22_PLAN.md §8) — resolver slug→wartość dla pól
 * core dokumentów, budowany z istniejącego `KartotekaEngineConfig.fields`. Analogiczne
 * resolvery dla kontrahenta/pracownika: `buildContractorCoreRecordValues`
 * (`components/contractors/ContractorDataTab.tsx`) / `buildEmployeeCoreRecordValues`
 * (`components/employees/EmployeeDataTab.tsx`) — słownik slug→stateKey tam jest statyczny
 * (nie data-driven jak `KartotekaEngineConfig`), bo te dwie kartoteki nie mają odpowiednika
 * tej konfiguracji.
 *
 * Zwraca `Record<string, unknown>` (nie od razu JSON-safe) — koercja do
 * string/number/boolean/null dla `ExecuteAdditionalViewDataInput.recordValues`
 * jest zadaniem `buildAdditionalViewRecordValues`
 * (`lib/additional-views/record-values.ts`), nie tego resolvera. Pola
 * strukturalne bez `stateKey` (`structuralNull`) są pomijane — nie mają wartości w stanie
 * formularza.
 *
 * Klucz stanu pola: jawny `stateKey` z configu (pilot, pismo, klasy gołe); config faktur i umowy
 * świadomie NIE ma `stateKey` (renderery same prowadzą widget i walidację formatu — patrz
 * `invoiceEngineConfig.test.tsx`), więc tam klucz wyliczamy mechanicznie ze sluga (snake_case →
 * camelCase, konwencja seeda) i bierzemy go TYLKO gdy istnieje w stanie formularza — slug bez
 * pary w formularzu (pole wyliczane, firma) jest pomijany, nie zgadywany. Zgodność slugów
 * z kluczami formularza pilnuje test configów faktur i umowy.
 */
export function buildCoreFieldRecordValues<TForm, TExtra = undefined>(
  config: Pick<KartotekaEngineConfig<TForm, TExtra>, 'fields'>,
  form: TForm,
): Record<string, unknown> {
  const state = form as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const [slug, fieldCfg] of Object.entries(config.fields)) {
    if (fieldCfg.stateKey) {
      result[slug] = state[fieldCfg.stateKey as string];
      continue;
    }
    if (fieldCfg.structuralNull) continue;
    const derivedKey = slugToCamel(slug);
    if (derivedKey in state) result[slug] = state[derivedKey];
  }
  return result;
}

/**
 * Buduje listę sprawdzeń formatu (oś typów pól) dla walidacji klienckiej silnika —
 * WSPÓLNA dla każdej kartoteki/dokumentu. Łączy dwa źródła:
 *  - pola CORE z configu (`config.fields`): typ = ground-truth `fieldType` z seeda,
 *    klucz błędu = `stateKey` (jak w `useFieldErrors`), wartość = `form[stateKey]`.
 *    Pola strukturalne (bez `stateKey`) pomijane.
 *  - pola DODATKOWE z katalogu (`catalogFields`, `!isCore`): typ = `fieldType` z
 *    odpowiedzi `/fields`, klucz = `slug`, wartość = `extValues[slug]`.
 *
 * Wynik podaje się do `collectFieldFormatErrors`. Kolejność: core (w kolejności
 * `config.fields`), potem ext — deterministyczna, żeby toast „pierwsze pole" był
 * stabilny. Puste wartości przepuszcza sam walidator (presence = osobna reguła).
 */
export function buildEngineFormatChecks<TForm, TExtra = undefined>(
  config: Pick<KartotekaEngineConfig<TForm, TExtra>, 'fields'>,
  form: TForm,
  catalogFields: EngineFormField[],
  extValues: Record<string, unknown>,
): FieldFormatCheck[] {
  const checks: FieldFormatCheck[] = [];
  for (const [, fieldCfg] of Object.entries(config.fields)) {
    if (!fieldCfg.stateKey) continue;
    checks.push({
      key: String(fieldCfg.stateKey),
      fieldType: fieldCfg.fieldType,
      value: (form as Record<string, unknown>)[fieldCfg.stateKey as string],
    });
  }
  for (const field of catalogFields) {
    if (field.isCore) continue;
    checks.push({ key: field.slug, fieldType: field.fieldType, value: extValues[field.slug] });
  }
  return checks;
}

/**
 * Buduje listę sprawdzeń REGUŁ z kreatora (min/max/wzorzec/długość/daty z
 * `validationRules`) — oś komplementarna do `buildEngineFormatChecks`, WSPÓLNA dla
 * kartotek i dokumentów. Reguły bierze z katalogu (`EngineFormField.validationRules`);
 * serwer egzekwuje je równolegle (require-ext.ts).
 *  - CORE: klucz = `stateKey` (jak format), reguły dopasowane z katalogu po slugu
 *    (klucz wpisu `config.fields` = slug), wartość = `form[stateKey]`.
 *  - DODATKOWE: klucz = `slug`, wartość = `extValues[slug]`.
 * Pola bez reguł pomijane. Wynik podaje się do `collectFieldRuleErrors`.
 */
export function buildEngineRuleChecks<TForm, TExtra = undefined>(
  config: Pick<KartotekaEngineConfig<TForm, TExtra>, 'fields'>,
  form: TForm,
  catalogFields: EngineFormField[],
  extValues: Record<string, unknown>,
): FieldRuleCheck[] {
  const rulesBySlug = new Map<string, Record<string, string>>();
  for (const field of catalogFields) {
    if (field.validationRules && Object.keys(field.validationRules).length > 0) {
      rulesBySlug.set(field.slug, field.validationRules);
    }
  }

  const checks: FieldRuleCheck[] = [];
  for (const [slug, fieldCfg] of Object.entries(config.fields)) {
    if (!fieldCfg.stateKey) continue;
    const rules = rulesBySlug.get(slug);
    if (!rules) continue;
    checks.push({
      key: String(fieldCfg.stateKey),
      fieldType: fieldCfg.fieldType,
      rules,
      value: (form as Record<string, unknown>)[fieldCfg.stateKey as string],
    });
  }
  for (const field of catalogFields) {
    if (field.isCore) continue;
    const rules = rulesBySlug.get(field.slug);
    if (!rules) continue;
    checks.push({
      key: field.slug,
      fieldType: field.fieldType,
      rules,
      value: extValues[field.slug],
    });
  }
  return checks;
}

/**
 * Zbiera obie osie walidacji klienckiej silnika (FORMAT po typie + REGUŁY z kreatora)
 * w JEDNĄ mapę `klucz błędu → gotowy polski komunikat`. Jedno wywołanie per strona
 * detalu (kartoteka/dokument) — wpina się do `useFieldErrors`. Format ma priorytet nad
 * regułą przy tym samym polu (błąd typu jest bardziej podstawowy). Pusta mapa = OK.
 */
export function collectEngineFieldErrors<TForm, TExtra = undefined>(
  config: Pick<KartotekaEngineConfig<TForm, TExtra>, 'fields'>,
  form: TForm,
  catalogFields: EngineFormField[],
  extValues: Record<string, unknown>,
  t: TFunction,
): Record<string, string> {
  const out: Record<string, string> = {};
  const formatErrors = collectFieldFormatErrors(
    buildEngineFormatChecks(config, form, catalogFields, extValues),
  );
  for (const [key, code] of Object.entries(formatErrors)) {
    out[key] = t(`common.validationIssue.${code}`);
  }
  const ruleErrors = collectFieldRuleErrors(
    buildEngineRuleChecks(config, form, catalogFields, extValues),
  );
  for (const [key, err] of Object.entries(ruleErrors)) {
    if (out[key]) continue; // format wygrywa przy tym samym polu
    out[key] = resolveRuleErrorMessage(err, t);
  }
  return out;
}
